import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Code2,
  MessageSquare,
  Scale,
  Package,
  ArrowRight,
} from "lucide-react";

import SpotlightCard from "../../../components/animations/SpotlightCard";
import Noise from "../../../components/animations/Noise";
import AnimatedHeading from "../../../components/animations/AnimatedHeading";

/* -------------------------------------------------------------------------- */
/* PARALLAX CARD                                                              */
/* -------------------------------------------------------------------------- */

const ParallaxCard = ({ children, className = "" }) => {
  const cardRef = useRef(null);

  const handleMove = (e) => {
    const card = cardRef.current;
    if (!card) return;

    // Disable mouse parallax on touch devices
    if (window.matchMedia("(hover: none)").matches) return;

    const rect = card.getBoundingClientRect();

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = (y - centerY) / 100;
    const rotateY = (centerX - x) / 100;

    card.style.transition = "transform .12s ease-out";

    card.style.transform = `
      perspective(1200px)
      rotateX(${rotateX}deg)
      rotateY(${rotateY}deg)
    `;
  };

  const resetParallax = () => {
    const card = cardRef.current;
    if (!card) return;

    card.style.transition =
      "transform .55s cubic-bezier(.23,1,.32,1)";

    card.style.transform =
      "perspective(1200px) rotateX(0deg) rotateY(0deg)";
  };

  return (
    <div
      ref={cardRef}
      className={`group h-full ${className}`}
      style={{
        transformStyle: "preserve-3d",
        willChange: "transform",
      }}
      onMouseMove={handleMove}
      onMouseLeave={resetParallax}
    >
      {children}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* SERVICES                                                                   */
/* -------------------------------------------------------------------------- */

const services = [
  {
    title: "Software Development",
    desc:
      "Custom software, web applications, mobile apps, UI/UX design, CRM solutions and enterprise platforms engineered for business growth.",
    icon: Code2,
    slug: "software-development",
    tags: ["Mobile", "Web", "UI/UX", "CRM", "MLM"],
  },

  {
    title: "API Services",
    desc:
      "Official WhatsApp Business API solutions including Business SMS Services and Secure OTP Services for seamless customer communication.",
    icon: MessageSquare,
    slug: "api",
    tags: ["Whatsapp api", "sms-otp Service"],
  },

  {
    title: "Legal Services",
    desc:
      "Complete compliance solutions including business registration, taxation, ISO certification and intellectual property services.",
    icon: Scale,
    slug: "legal-services",
    tags: ["Registration", "Taxation", "ISO", "IPR"],
  },

  {
    title: "Product Development",
    desc:
      "Innovative digital product engineering for startups and enterprises. Launching soon.",
    icon: Package,
    slug: "product-development",
    comingSoon: true,
    tags: ["Coming Soon"],
  },
];

/* -------------------------------------------------------------------------- */
/* COMPONENT                                                                  */
/* -------------------------------------------------------------------------- */

export default function ExploreServices() {
  const navigate = useNavigate();

  /* ------------------------------------------------------------------------ */
  /* NAVIGATION                                                               */
  /* ------------------------------------------------------------------------ */

  const handleNavigate = (service) => {
    if (service.comingSoon) return;

    navigate(`/services/${service.slug}`);

    requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    });
  };

  return (
    <section
      className="
        relative
        overflow-hidden

        bg-[var(--bg-primary)]
        text-[var(--text-primary)]
        font-saira

        px-3
        py-8

        min-[375px]:px-4

        sm:px-5
        sm:py-9

        md:px-7
        md:py-10

        lg:px-10
        lg:py-11

        xl:py-12
      "
    >
      {/* ================================================================== */}
      {/* NOISE BACKGROUND                                                    */}
      {/* ================================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          inset-0
          z-[1]
          opacity-70
        "
      >
        <Noise
          patternSize={250}
          patternScaleX={2}
          patternScaleY={2}
          patternRefreshInterval={2}
          patternAlpha={12}
        />
      </div>

      {/* ================================================================== */}
      {/* LEFT BACKGROUND GLOW                                                */}
      {/* ================================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-40
          top-20

          h-[180px]
          w-[180px]

          rounded-full
          bg-[var(--brand-primary)]/[0.05]
          blur-[90px]

          min-[375px]:h-[210px]
          min-[375px]:w-[210px]

          sm:h-[260px]
          sm:w-[260px]
          sm:blur-[110px]

          lg:h-[320px]
          lg:w-[320px]
          lg:blur-[130px]
        "
      />

      {/* ================================================================== */}
      {/* RIGHT BACKGROUND GLOW                                               */}
      {/* ================================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -right-40
          bottom-10

          h-[180px]
          w-[180px]

          rounded-full
          bg-[var(--brand-primary)]/[0.04]
          blur-[90px]

          min-[375px]:h-[210px]
          min-[375px]:w-[210px]

          sm:h-[260px]
          sm:w-[260px]
          sm:blur-[110px]

          lg:h-[320px]
          lg:w-[320px]
          lg:blur-[130px]
        "
      />

      {/* ================================================================== */}
      {/* MAIN CONTAINER                                                      */}
      {/* ================================================================== */}

      <div
        className="
          relative
          z-10
          mx-auto
          w-full
          max-w-7xl
        "
      >
        {/* ================================================================= */}
        {/* HEADER                                                             */}
        {/* ================================================================= */}

        <div
          className="
            mx-auto
            mb-6
            w-full
            max-w-3xl
            px-1
            text-center

            sm:mb-7
            sm:px-2

            md:mb-8

            lg:mb-9
          "
        >
          {/* ---------------------------------------------------------------- */}
          {/* EYEBROW                                                          */}
          {/* ---------------------------------------------------------------- */}

          <p
            className="
              text-[8px]
              font-bold
              uppercase
              tracking-[0.22em]
              text-[var(--brand-primary)]

              min-[375px]:text-[9px]

              sm:text-[10px]
              sm:tracking-[0.24em]

              md:text-xs
            "
          >
            [ Explore Services ]
          </p>

          {/* ---------------------------------------------------------------- */}
          {/* HEADING                                                          */}
          {/* ---------------------------------------------------------------- */}

          <AnimatedHeading
            as="h2"
            parts={["Smart Solutions for Modern Business"]}
            className="
              mt-2.5

              text-[22px]
              font-semibold
              leading-[1.15]
              tracking-tight
              text-[var(--text-primary)]

              min-[375px]:text-2xl

              sm:mt-3
              sm:text-3xl

              md:text-4xl

              lg:text-[44px]
              lg:leading-tight
            "
          />

          {/* ---------------------------------------------------------------- */}
          {/* DESCRIPTION                                                      */}
          {/* ---------------------------------------------------------------- */}

          <p
            className="
              mx-auto
              mt-2
              w-full
              max-w-[600px]
              px-1

              text-[10px]
              leading-[1.55]
              text-[var(--text-muted)]

              min-[375px]:text-[11px]

              sm:mt-2.5
              sm:px-0
              sm:text-[12px]
              sm:leading-5

              md:text-[14px]
              md:leading-6

              lg:text-[15px]
            "
          >
            We combine technology, automation and business expertise to
            deliver scalable digital solutions that help organizations
            grow faster and operate smarter.
          </p>
        </div>

        {/* ================================================================= */}
        {/* SERVICES GRID                                                      */}
        {/* ================================================================= */}

        <div
          className="
            grid
            w-full
            grid-cols-1
            gap-3

            min-[375px]:gap-3.5

            sm:gap-4

            md:grid-cols-2
            md:gap-4

            lg:gap-5
          "
        >
          {services.map((service) => (
            <div
              key={service.slug}
              className="
                h-full
                min-w-0
              "
              onClick={() => handleNavigate(service)}
            >
              <ParallaxCard
                className={`
                  h-full
                  ${
                    service.comingSoon
                      ? "cursor-default"
                      : "cursor-pointer"
                  }
                `}
              >
                <SpotlightCard
                  className="
                    relative
                    flex
                    h-full
                    min-h-[205px]
                    w-full
                    overflow-hidden

                    rounded-xl

                    border
                    border-[var(--border-primary)]

                    /*
                     * IMPORTANT:
                     * This is changed from var(--card-bg)
                     * to var(--bg-tertiary).
                     *
                     * Light mode:
                     * #f1f1f1
                     *
                     * Dark mode:
                     * #141414
                     */
                    bg-[var(--bg-tertiary)]

                    p-3.5
                    text-center

                    transition-all
                    duration-500

                    hover:border-[var(--brand-primary)]/30

                    /*
                     * Keep hover slightly different from
                     * the normal card background.
                     */
                    hover:bg-[var(--card-bg-secondary)]

                    min-[375px]:min-h-[210px]
                    min-[375px]:p-4

                    sm:min-h-[215px]
                    sm:rounded-2xl
                    sm:p-5

                    md:min-h-[220px]
                    md:p-5

                    lg:min-h-[225px]
                    lg:p-5.5

                    xl:min-h-[230px]
                  "
                  /*
                   * Brand color is intentionally fixed.
                   * Your brand remains orange in both themes.
                   */
                  tailwindColor="#F97316"
                  alpha={0.25}
                  radius={360}
                >
                  {/* ======================================================= */}
                  {/* CARD CONTENT                                             */}
                  {/* ======================================================= */}

                  <div
                    className="
                      flex
                      h-full
                      min-h-0
                      w-full
                      flex-col
                      items-center
                    "
                  >
                    {/* ===================================================== */}
                    {/* CENTER CONTENT                                         */}
                    {/* ===================================================== */}

                    <div
                      className="
                        flex
                        w-full
                        flex-1
                        flex-col
                        items-center
                        justify-start
                      "
                    >
                      {/* --------------------------------------------------- */}
                      {/* ICON                                                  */}
                      {/* --------------------------------------------------- */}

                      <div
                        className="
                          flex
                          h-10
                          w-10
                          shrink-0
                          items-center
                          justify-center

                          rounded-lg

                          bg-[var(--brand-primary)]

                          transition-all
                          duration-500

                          group-hover:rotate-6
                          group-hover:bg-[var(--text-primary)]

                          min-[375px]:h-11
                          min-[375px]:w-11

                          sm:h-12
                          sm:w-12
                          sm:rounded-xl
                        "
                      >
                        <service.icon
                          size={20}
                          strokeWidth={2}
                          className="
                            text-[var(--bg-primary)]

                            min-[375px]:h-[21px]
                            min-[375px]:w-[21px]

                            sm:h-[22px]
                            sm:w-[22px]
                          "
                        />
                      </div>

                      {/* --------------------------------------------------- */}
                      {/* TITLE                                                 */}
                      {/* --------------------------------------------------- */}

                      <h3
                        className="
                          mt-2.5
                          w-full
                          max-w-[620px]
                          px-1

                          text-[16px]
                          font-semibold
                          leading-[1.2]

                          text-[var(--text-primary)]

                          transition-colors
                          duration-300

                          group-hover:text-[var(--text-primary)]

                          min-[375px]:text-[17px]

                          sm:mt-3
                          sm:text-[19px]

                          md:text-xl

                          lg:text-[21px]
                        "
                      >
                        {service.title}
                      </h3>

                      {/* --------------------------------------------------- */}
                      {/* DESCRIPTION                                           */}
                      {/* --------------------------------------------------- */}

                      <p
                        className="
                          mx-auto
                          mt-1.5
                          w-full
                          max-w-[700px]
                          px-1

                          text-[10px]
                          leading-[1.5]

                          text-[var(--text-muted)]

                          min-[375px]:text-[11px]

                          sm:mt-2
                          sm:px-2
                          sm:text-[12px]
                          sm:leading-5

                          md:max-w-[680px]
                          md:text-[13px]

                          lg:max-w-[700px]
                          lg:text-[14px]
                          lg:leading-5
                        "
                      >
                        {service.desc}
                      </p>

                      {/* --------------------------------------------------- */}
                      {/* TAGS                                                 */}
                      {/* --------------------------------------------------- */}

                      <div
                        className="
                          mt-2
                          flex
                          w-full
                          flex-wrap
                          items-center
                          justify-center
                          gap-1
                          px-1

                          min-[375px]:mt-2.5

                          sm:mt-3
                          sm:gap-1.5

                          md:px-2
                        "
                      >
                        {service.tags.map((tag) => (
                          <span
                            key={tag}
                            className={`
                              rounded-full

                              px-1.5
                              py-1

                              text-[7px]
                              font-medium
                              leading-none
                              whitespace-nowrap

                              transition-all
                              duration-300

                              min-[375px]:px-2
                              min-[375px]:text-[8px]

                              sm:px-2.5
                              sm:py-1
                              sm:text-[9px]

                              md:text-[10px]

                              ${
                                tag === "Coming Soon"
                                  ? `
                                    border
                                    border-[var(--brand-primary)]/40
                                    bg-[var(--brand-primary)]/15
                                    text-[var(--brand-primary)]
                                  `
                                  : `
                                    border
                                    border-[var(--border-primary)]
                                    bg-[var(--bg-primary)]/40
                                    text-[var(--text-secondary)]
                                  `
                              }
                            `}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* ===================================================== */}
                    {/* BOTTOM CTA                                             */}
                    {/* ===================================================== */}

                    <div
                      className="
                        mt-3
                        flex
                        w-full
                        shrink-0
                        items-center
                        justify-between
                        pt-2

                        min-[375px]:mt-3.5

                        sm:mt-4
                        sm:pt-2.5
                      "
                    >
                      {/* --------------------------------------------------- */}
                      {/* CTA TEXT                                             */}
                      {/* --------------------------------------------------- */}

                      <span
                        className={`
                          text-[9px]
                          font-medium

                          transition-colors
                          duration-300

                          min-[375px]:text-[10px]

                          sm:text-[11px]

                          md:text-xs

                          ${
                            service.comingSoon
                              ? "text-[var(--text-muted)]"
                              : "text-[var(--brand-primary)] group-hover:text-[var(--text-primary)]"
                          }
                        `}
                      >
                        {service.comingSoon
                          ? "Available Soon"
                          : "Explore Service"}
                      </span>

                      {/* --------------------------------------------------- */}
                      {/* CTA BUTTON                                           */}
                      {/* --------------------------------------------------- */}

                      <div
                        className={`
                          flex
                          h-7
                          w-7
                          shrink-0
                          items-center
                          justify-center

                          rounded-full
                          border

                          transition-all
                          duration-500

                          min-[375px]:h-8
                          min-[375px]:w-8

                          sm:h-9
                          sm:w-9

                          md:h-10
                          md:w-10

                          ${
                            service.comingSoon
                              ? `
                                border-[var(--border-primary)]
                                bg-[var(--bg-tertiary)]
                              `
                              : `
                                border-[var(--border-primary)]
                                bg-[var(--bg-tertiary)]

                                group-hover:border-[var(--brand-primary)]
                                group-hover:bg-[var(--brand-primary)]
                              `
                          }
                        `}
                      >
                        <ArrowRight
                          size={14}
                          className={`
                            transition-all
                            duration-500

                            min-[375px]:h-[15px]
                            min-[375px]:w-[15px]

                            sm:h-4
                            sm:w-4

                            ${
                              service.comingSoon
                                ? "text-[var(--text-muted)]"
                                : "text-[var(--text-primary)] group-hover:translate-x-1 group-hover:text-[var(--bg-primary)]"
                            }
                          `}
                        />
                      </div>
                    </div>
                  </div>
                </SpotlightCard>
              </ParallaxCard>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}